import { QueryCache, QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { timerSpansQueryKey } from "./queryKeys";

// Global error handler — fires once per failed query regardless of observer count.
// Satisfies sentry-logging.md: all errors captured without manual try/catch per hook.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error, {
        extra: {
          queryKey: query.queryKey,
          queryHash: query.queryHash,
        },
      });
    },
  }),
  defaultOptions: {
    queries: {
      // staleTime intentionally absent here — set per-key-family below via setQueryDefaults
      gcTime: 10 * 60 * 1000,      // 10 min — keep unused data in memory after unmount
      refetchOnWindowFocus: false,  // billing data updates at most once daily via sync
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Per-key-family stale times — single source of truth (H4 fix).
// Hooks that use these keys do NOT define staleTime themselves.

// YoY tab
queryClient.setQueryDefaults(["yoy-summary"], { staleTime: 2 * 60 * 1000 });
queryClient.setQueryDefaults(["yoy-timeseries"], { staleTime: 2 * 60 * 1000 });
queryClient.setQueryDefaults(["daily-view"], { staleTime: 5 * 60 * 1000 });
queryClient.setQueryDefaults(["go-live-dates"], { staleTime: 30 * 60 * 1000 });

// Shared
queryClient.setQueryDefaults(["professionals"], { staleTime: 5 * 60 * 1000 });

// Timekeeper Performance tab
queryClient.setQueryDefaults(["available-weeks"], { staleTime: 30 * 60 * 1000 });
queryClient.setQueryDefaults(["timekeeper-perf"], { staleTime: 5 * 60 * 1000 });

// Hourglass Stats tab
queryClient.setQueryDefaults(["export-stats"], { staleTime: 5 * 60 * 1000 });
queryClient.setQueryDefaults(["dashboard-metrics"], { staleTime: 5 * 60 * 1000 });

// Utilization tab
queryClient.setQueryDefaults(["utilization"], { staleTime: 5 * 60 * 1000 });

// Support inbox
queryClient.setQueryDefaults(["feedback"], { staleTime: 2 * 60 * 1000 });

// Smart timers — always considered stale so focus/poll refetches converge tabs
// on the server's single running timer as fast as possible.
queryClient.setQueryDefaults(timerSpansQueryKey, { staleTime: 0 });

// Usage metrics (currently no active UI consumer — future use)
queryClient.setQueryDefaults(["usage-metrics"], { staleTime: 5 * 60 * 1000 });
